import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { billingApi } from '../services/api';
import Modal, { useModal } from '../components/Modal';
import { missingBillingFields } from './PaymentPage';
import AddressAutocomplete from '../components/AddressAutocomplete';
import LoadingOverlay from '../components/LoadingOverlay';

const Req = () => <span style={{ color: 'var(--error)', marginLeft: 2 }}>*</span>;

const PROVINCE = ['AG','AL','AN','AO','AQ','AR','AP','AT','AV','BA','BT','BL','BN','BG','BI','BO','BZ','BS','BR','CA','CL','CB','CI','CE','CT','CZ','CH','CO','CS','CR','KR','CN','EN','FM','FE','FI','FG','FC','FR','GE','GO','GR','IM','IS','SP','LT','LE','LC','LI','LO','LU','MC','MN','MS','MT','ME','MI','MO','MB','NA','NO','NU','OG','OT','OR','PD','PA','PR','PV','PG','PU','PE','PC','PI','PT','PN','PZ','PO','RG','RA','RC','RE','RI','RN','RM','RO','SA','SS','SV','SI','SR','SO','TA','TE','TR','TO','TP','TN','TV','TS','UD','VA','VE','VB','VC','VR','VV','VI','VT'];

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return');
  const { user, refreshUser } = useAuth();
  const { modal, show, close } = useModal();

  const [form, setForm] = useState({
    billing_type: user?.billing_type || 'privato',
    billing_first_name: user?.billing_first_name || '',
    billing_last_name: user?.billing_last_name || '',
    billing_company: user?.billing_company || '',
    vat_number: user?.vat_number || '',
    tax_code: user?.tax_code || '',
    billing_address: user?.billing_address || '',
    billing_city: user?.billing_city || '',
    billing_zip: user?.billing_zip || '',
    billing_province: user?.billing_province || '',
    sdi_code: user?.sdi_code || '',
    pec_email: user?.pec_email || '',
  });

  const [saving, setSaving] = useState(false);

  const upd = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  const saveBilling = async () => {
    const preview = { ...form, billing_type: form.billing_type || 'privato' };
    const missing = missingBillingFields(preview);
    if (missing.length > 0) {
      show({
        title: 'Dati incompleti',
        message: `Per emettere una fattura fiscalmente valida sono necessari: ${missing.join(', ')}.`,
        type: 'warning',
        onConfirm: close,
      });
      return;
    }
    setSaving(true);
    try {
      await billingApi.update(form);
      await refreshUser();
      show({
        title: 'Salvato',
        message: 'Dati di fatturazione aggiornati.',
        type: 'success',
        confirmText: returnUrl ? 'Continua al pagamento →' : 'OK',
        onConfirm: () => { close(); if (returnUrl) navigate(returnUrl); },
      });
    } catch {
      show({ title: 'Errore', message: 'Impossibile salvare. Riprova.', type: 'error', onConfirm: close });
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="page">
      {saving && <LoadingOverlay title="Salvataggio dei dati di fatturazione..." subtitle="Un momento" icon="receipt_long" />}
      {modal && (
        <Modal isOpen title={modal.title} message={modal.message} type={modal.type}
          confirmText={modal.confirmText} cancelText={modal.cancelText}
          onConfirm={modal.onConfirm} onCancel={modal.onCancel ?? close} />
      )}

      <div className="top-nav" style={{ marginBottom: 0 }}>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <span className="mi mi-20">arrow_back</span>
        </button>
        <span className="nav-title">Dati di fatturazione</span>
      </div>

      {/* ── Dati di fatturazione ── */}
      <div>
        <span className="section-label">Dati di fatturazione</span>
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Tipo */}
            <div className="form-group">
              <label className="form-label">Tipo intestatario</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['privato', 'azienda'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => upd('billing_type', t)}
                    style={{
                      flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', border: '1.5px solid',
                      borderColor: form.billing_type === t ? 'var(--primary)' : 'var(--border-md)',
                      background: form.billing_type === t ? 'var(--primary-sub)' : 'var(--s2)',
                      color: form.billing_type === t ? 'var(--primary)' : 'var(--text-2)',
                    }}
                  >
                    {t === 'privato' ? 'Privato' : 'Azienda'}
                  </button>
                ))}
              </div>
            </div>

            {form.billing_type === 'privato' ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Nome<Req /></label>
                    <input className="form-input" value={form.billing_first_name} onChange={e => upd('billing_first_name', e.target.value)} placeholder="Mario" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cognome<Req /></label>
                    <input className="form-input" value={form.billing_last_name} onChange={e => upd('billing_last_name', e.target.value)} placeholder="Rossi" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Codice Fiscale<Req /></label>
                  <input className="form-input" value={form.tax_code} onChange={e => upd('tax_code', e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501Z" maxLength={16} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }} />
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Ragione Sociale<Req /></label>
                  <input className="form-input" value={form.billing_company} onChange={e => upd('billing_company', e.target.value)} placeholder="Pizzeria Da Mario S.r.l." />
                </div>
                <div className="form-group">
                  <label className="form-label">Partita IVA<Req /></label>
                  <input className="form-input" value={form.vat_number} onChange={e => upd('vat_number', e.target.value)} placeholder="IT12345678901" maxLength={13} />
                </div>
                <div className="form-group">
                  <label className="form-label">Codice Fiscale <span style={{ color: 'var(--text-3)', fontSize: 11 }}>(se diverso da P.IVA)</span></label>
                  <input className="form-input" value={form.tax_code} onChange={e => upd('tax_code', e.target.value.toUpperCase())} placeholder="Opzionale" maxLength={16} style={{ textTransform: 'uppercase' }} />
                </div>
              </>
            )}

            <div className="form-group">
              <AddressAutocomplete
                label="Indirizzo"
                value={form.billing_address}
                onChange={v => upd('billing_address', v)}
                placeholder="Via Roma 1"
                onSelect={parsed => {
                  if (parsed.address)  upd('billing_address',  parsed.address);
                  if (parsed.city)     upd('billing_city',     parsed.city);
                  if (parsed.zip)      upd('billing_zip',      parsed.zip);
                  if (parsed.province) upd('billing_province', parsed.province);
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">CAP<Req /></label>
                <input className="form-input" value={form.billing_zip} onChange={e => upd('billing_zip', e.target.value)} placeholder="09100" maxLength={5} inputMode="numeric" />
              </div>
              <div className="form-group">
                <label className="form-label">Città<Req /></label>
                <input className="form-input" value={form.billing_city} onChange={e => upd('billing_city', e.target.value)} placeholder="Cagliari" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Provincia<Req /></label>
              <select
                className="form-input"
                value={form.billing_province}
                onChange={e => upd('billing_province', e.target.value)}
                style={{ color: form.billing_province ? 'var(--text-1)' : 'var(--text-3)' }}
              >
                <option value="">Seleziona provincia</option>
                {PROVINCE.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* Fatturazione elettronica: SDI o PEC — solo per azienda */}
            {form.billing_type === 'azienda' && (
              <div style={{ marginTop: 4, padding: '12px 14px', background: 'var(--s2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 10 }}>
                  Fatturazione elettronica <span style={{ color: 'var(--warning)', fontSize: 11 }}>(obbligatorio almeno uno)</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Codice Destinatario SDI <span style={{ color: 'var(--text-3)', fontSize: 11 }}>(7 caratteri)</span></label>
                    <input
                      className="form-input"
                      value={form.sdi_code}
                      onChange={e => upd('sdi_code', e.target.value.toUpperCase())}
                      placeholder="0000000"
                      maxLength={7}
                      style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}
                    />
                  </div>
                  <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-3)' }}>oppure</div>
                  <div className="form-group">
                    <label className="form-label">PEC</label>
                    <input
                      className="form-input"
                      type="email"
                      value={form.pec_email}
                      onChange={e => upd('pec_email', e.target.value)}
                      placeholder="fatture@pec.esempio.it"
                    />
                  </div>
                </div>
              </div>
            )}

            <button className="btn btn-primary" onClick={saveBilling} disabled={saving} style={{ marginTop: 4 }}>
              <span className="mi mi-18">save</span> Salva dati fatturazione
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
